import type { ErrorEvent } from "@sentry/react";

// Pin the artifact seen in XAN-BUSINESS-5. A different build needs fresh evidence.
const CLEANUP_ASSET_RE = /^(?:https?:\/\/[^/?#]+)?\/assets\/cleanupQuarantine-Bnmu56FB\.js$/;

/**
 * Contain only XAN-BUSINESS-5's unhandled IndexedDB reopen rejection.
 * Replicache's IDBStore rejects onupgradeneeded after deletion and explicitly
 * continues in memory until reload (rocicorp/mono, replicache/src/kv/idb-store.ts).
 * This checkout has no Replicache client to recover; the captured artifact's
 * owner is unknown. Reloading or deleting storage could lose pending work.
 * Do not classify this as extension noise or ignore IndexedDB errors by name.
 */
export function isKnownReplicacheStorageLoss(event: ErrorEvent): boolean {
  const exceptions = event.exception?.values;
  if (exceptions?.length !== 1) {
    return false;
  }

  const exception = exceptions[0];
  if (
    exception.type !== "IDBNotFoundError" ||
    exception.mechanism?.handled === true ||
    (exception.mechanism?.handled !== false &&
      event.extra?.source !== "unhandled_promise_rejection")
  ) {
    return false;
  }

  // Keep mixed/application stacks and incomplete evidence reportable. The
  // message may already be redacted, so match the captured throw site instead.
  const frames = exception.stacktrace?.frames;
  if (frames?.length !== 1) {
    return false;
  }
  const frame = frames[0];
  return (
    frame.function === "i.onupgradeneeded" &&
    typeof frame.filename === "string" &&
    CLEANUP_ASSET_RE.test(frame.filename)
  );
}
